import { useState, useMemo } from 'react';
import { FileText } from 'lucide-react';
import { useAuth } from '@dashboard/hooks/useAuth';
import useStripeInvoices from './useStripeInvoices';
import InvoiceMetricCards from './InvoiceMetricCards';
import InvoiceTable from './InvoiceTable';
import InvoiceCreateModal from './InvoiceCreateModal';
import InvoiceDetail from './InvoiceDetail';
import type { Invoice, InvoiceStatus } from './useStripeInvoices';

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'sent', label: 'Sent' },
  { key: 'paid', label: 'Paid' },
  { key: 'draft', label: 'Draft' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'cancelled', label: 'Cancelled' },
];

export default function StripeInvoices() {
  const { user } = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [search, setSearch] = useState('');
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);

  const {
    invoices,
    filtered,
    loading,
    error,
    statusFilter,
    setStatusFilter,
    createInvoice,
    updateStatus,
  } = useStripeInvoices(user?.id);

  const searchFiltered = useMemo(() => {
    if (!search) return filtered;
    const q = search.toLowerCase();
    return filtered.filter(
      (inv) =>
        inv.number.toLowerCase().includes(q) ||
        inv.recipient.toLowerCase().includes(q) ||
        inv.description.toLowerCase().includes(q),
    );
  }, [filtered, search]);

  function handleSelect(inv: Invoice) {
    setSelectedInvoice(inv);
  }

  async function handleUpdateStatus(id: string, status: InvoiceStatus) {
    try {
      await updateStatus(id, status);
      if (selectedInvoice?.id === id) {
        setSelectedInvoice((prev) => prev ? { ...prev, status } : null);
      }
    } catch { /* errors handled in hook */ }
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1
          className="font-sans text-lg font-medium"
          style={{ color: 'var(--dash-text-primary)' }}
        >
          Stripe Invoices
        </h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded bg-white text-zinc-900 font-sans text-sm font-medium hover:bg-zinc-200 transition-colors"
        >
          <span className="text-base leading-none">+</span>
          New Invoice
        </button>
      </div>

      {invoices.length > 0 && <InvoiceMetricCards invoices={invoices} />}

      {invoices.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div
            className="flex gap-1 p-0.5 rounded overflow-x-auto"
            style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-input)' }}
          >
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                className="px-3 py-1.5 rounded font-mono text-xs transition-colors whitespace-nowrap"
                style={{
                  background: statusFilter === tab.key ? 'var(--dash-hover-bg)' : 'transparent',
                  color:
                    statusFilter === tab.key
                      ? 'var(--dash-text-secondary)'
                      : hoveredTab === tab.key
                        ? 'var(--dash-text-tertiary)'
                        : 'var(--dash-text-dim)',
                }}
                onMouseEnter={() => setHoveredTab(tab.key)}
                onMouseLeave={() => setHoveredTab(null)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="relative flex-1">
            <span
              className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm"
              style={{ color: 'var(--dash-text-dim)' }}
            >
              /
            </span>
            <input
              type="text"
              placeholder="Search invoices..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-7 pr-3 py-2 rounded font-sans text-sm focus:outline-none transition-colors"
              style={{
                border: '1px solid var(--dash-border)',
                background: 'var(--dash-input)',
                color: 'var(--dash-text-secondary)',
              }}
            />
          </div>
        </div>
      )}

      {loading ? (
        <div
          className="rounded p-6"
          style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
        >
          <div className="animate-pulse space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-4 rounded w-full"
                style={{ background: 'var(--dash-hover-bg)' }}
              />
            ))}
          </div>
        </div>
      ) : error ? (
        <div
          className="rounded p-6"
          style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
        >
          <p className="font-sans text-sm text-red-400">{error}</p>
        </div>
      ) : searchFiltered.length === 0 && !search && statusFilter === 'all' ? (
        <div
          className="rounded p-10 text-center"
          style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
        >
          <div className="flex justify-center mb-4 opacity-20">
            <FileText size={40} />
          </div>
          <p
            className="font-mono text-sm mb-2"
            style={{ color: 'var(--dash-text-muted)' }}
          >
            No invoices yet
          </p>
          <p
            className="font-sans text-sm max-w-md mx-auto leading-relaxed mb-5"
            style={{ color: 'var(--dash-text-faint)' }}
          >
            Create invoices to bill your customers via Stripe. Each invoice is emailed directly to the recipient with a secure payment link.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded bg-white text-zinc-900 font-sans text-sm font-medium hover:bg-zinc-200 transition-colors"
          >
            <span className="text-base leading-none">+</span>
            Create Your First Invoice
          </button>
        </div>
      ) : searchFiltered.length === 0 ? (
        <div
          className="rounded p-8 text-center"
          style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
        >
          <p className="font-sans text-sm" style={{ color: 'var(--dash-text-muted)' }}>
            No invoices match your filters.
          </p>
        </div>
      ) : (
        <InvoiceTable
          invoices={searchFiltered}
          onSelect={handleSelect}
          onUpdateStatus={handleUpdateStatus}
        />
      )}

      <InvoiceDetail
        invoice={selectedInvoice}
        onClose={() => setSelectedInvoice(null)}
        onUpdateStatus={handleUpdateStatus}
      />

      <InvoiceCreateModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={createInvoice}
      />
    </div>
  );
}
